import { ChangeEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import { ErrorNotice } from '../ui/States';
import { fmt } from '../../lib/format';
import { errorMessage, selectClass } from '../../lib/programs';
import { EventDetail, dayLabel } from '../../lib/events';
import { UPLOAD_ACCEPT, UploadFile, readUpload, stamp } from './externalFiles';
import Help from '../ui/Help';

interface Result {
  dryRun: boolean;
  day: string;
  attendees: number;
  members: number;
  matchedByEmail: number;
  matchedByName: number;
  ambiguousName: number;
  contactsMatched: number;
  contactsCheckedIn: number;
  unmatchedWithEmail: number;
  unmatchedNoEmail: number;
  alreadyImported: number;
  attendanceCreated: number;
  triageAdded: number;
  invitees: number;
  warnings: string[];
}

export default function PheedloopCheckinUpload({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const detail = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  const state = useQuery<{ lastCheckinUploadAt: string | null }>(
    ['event-pheedloop', eventId],
    async () => (await api.get(`/events/${eventId}/pheedloop`)).data.data,
  );
  const days = detail.data?.event.days ?? [];
  const [day, setDay] = useState('');
  const [file, setFile] = useState<UploadFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const chosenDay = day || (days.length === 1 ? days[0].date : '');
  const send = async (confirm: boolean) =>
    (
      await api.post(`/events/${eventId}/pheedloop/checkins`, {
        fileName: file?.name,
        content: file?.content,
        day: chosenDay,
        confirm,
      })
    ).data.data as Result;
  const preview = useMutation(() => send(false));
  const save = useMutation(() => send(true), {
    onSuccess: () => {
      queryClient.invalidateQueries('event');
      queryClient.invalidateQueries('event-stats');
      queryClient.invalidateQueries('event-pheedloop');
    },
  });
  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    setFile(null);
    setFileError(null);
    preview.reset();
    save.reset();
    if (!f) return;
    try {
      setFile(await readUpload(f));
    } catch (err) {
      setFileError((err as Error).message);
    }
  };
  const r = save.data ?? preview.data;
  return (
    <Panel>
      <PanelHeader
        title="PheedLoop check-ins"
        note={state.data ? `last upload ${stamp(state.data.lastCheckinUploadAt)}` : undefined}
      />
      <PanelBody className="text-small">
        <Help>
          After the event, export the attendee or check-in report from PheedLoop (.csv or .xlsx), choose the day it
          covers, and upload it. Members are matched by email, then by exact name; contacts on the invitee list by
          email. Anyone else goes to the unmatched-attendee triage on the Webinars page; nobody is created. Uploading
          the same report again changes nothing.
        </Help>
        {canEdit ? (
          <div className="mt-3 flex flex-wrap gap-3 items-center">
            <select
              className={selectClass}
              value={chosenDay}
              onChange={(e) => {
                setDay(e.target.value);
                preview.reset();
                save.reset();
              }}
            >
              <option value="">Choose the day</option>
              {days.map((d) => (
                <option key={d.date} value={d.date}>
                  {dayLabel(d.date)}
                  {d.label ? ` · ${d.label}` : ''}
                </option>
              ))}
            </select>
            <input type="file" accept={UPLOAD_ACCEPT} onChange={pick} className="text-small" />
          </div>
        ) : null}
        {canEdit && file ? (
          <div className="flex gap-2 mt-3">
            <Button size="sm" disabled={!chosenDay || preview.isLoading} onClick={() => preview.mutate()}>
              Preview
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!preview.data || save.isLoading || save.isSuccess}
              onClick={() => save.mutate()}
            >
              Record attendance
            </Button>
          </div>
        ) : null}
        {fileError ? (
          <div className="mt-3">
            <ErrorNotice>{fileError}</ErrorNotice>
          </div>
        ) : null}
        {preview.error || save.error ? (
          <div className="mt-3">
            <ErrorNotice>
              {errorMessage(preview.error || save.error, 'Could not read the PheedLoop report.')}
            </ErrorNotice>
          </div>
        ) : null}
        {r ? (
          <div className="mt-3 border-t border-line pt-3">
            <div className="text-small text-ink-2 mb-1">
              {r.dryRun ? 'Preview, nothing saved yet' : 'Recorded'} · {dayLabel(r.day)}
            </div>
            <p>
              {fmt(r.attendees)} checked in · {fmt(r.members)} members ({fmt(r.matchedByEmail)} by email,{' '}
              {fmt(r.matchedByName)} by name) · {fmt(r.contactsMatched)} contacts on the list ·{' '}
              {fmt(Math.max(0, r.unmatchedWithEmail - r.contactsMatched))} to the triage
              {r.unmatchedNoEmail ? ` · ${fmt(r.unmatchedNoEmail)} without an email or a unique name` : ''}
            </p>
            {r.ambiguousName ? <p>{fmt(r.ambiguousName)} name(s) match more than one member: not recorded.</p> : null}
            {!r.dryRun ? (
              <p>
                {fmt(r.attendanceCreated)} new attendance record(s) · {fmt(r.invitees + r.contactsCheckedIn)} invitee
                check-in(s) · {fmt(r.triageAdded)} added to the triage
              </p>
            ) : r.alreadyImported ? (
              <p>{fmt(r.alreadyImported)} already recorded for this day.</p>
            ) : null}
            {r.warnings.map((w) => (
              <p key={w} className="text-ink-2">
                {w}
              </p>
            ))}
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}
